import React, { JSX, useEffect, useRef, useState } from "react";

interface Column {
  title: string;
  icon: string;
  score: number;
  averageScore?: number;
  [key: string]: any;
}

interface ComparisonArea {
  high: number;
  low: number;
}

interface LineChartProps {
  columns: Column[];
  comparisonArea?: ComparisonArea[];
  comparisonLine?: number[];
  height?: number;
  onColumnClick?: (column: Column) => void;
  renderScore?: (column: Column, x: number, y: number) => JSX.Element;
  renderColumnFooter?: (
    column: Column,
    index: number,
    isHovered?: boolean
  ) => JSX.Element;
  fixedWidth?: number;
  paddings: { top?: number; right?: number; bottom?: number; left?: number };
}

const LineChart: React.FC<LineChartProps> = ({
  columns,
  comparisonArea,
  comparisonLine,
  height = 300,
  onColumnClick,
  renderScore,
  renderColumnFooter,
  fixedWidth,
  paddings,
}) => {
  const [width, setWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);
  const padding = {
    top: 16,
    right: 0,
    bottom: 40,
    left: 0,
    ...paddings,
  };

  useEffect(() => {
    if (fixedWidth) {
      setWidth(fixedWidth);
      return;
    }

    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [fixedWidth]);

  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const columnWidth = chartWidth / columns.length;

  const xScale = (index: number) => columnWidth * (index + 0.5) + padding.left;
  const yScale = (score: number) =>
    chartHeight - (score / 100) * chartHeight + padding.top;

  const createSmoothLine = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return "";

    const lineCommands: string[] = [];
    lineCommands.push(`M ${points[0].x} ${points[0].y}`);

    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const midX = (current.x + next.x) / 2;

      lineCommands.push(
        `C ${midX} ${current.y}, ${midX} ${next.y}, ${next.x} ${next.y}`
      );
    }

    return lineCommands.join(" ");
  };

  const linePoints = columns.map((col, i) => ({
    x: xScale(i),
    y: yScale(col.score),
  }));

  const linePath = createSmoothLine(linePoints);

  const comparisonLinePath = comparisonLine?.length
    ? createSmoothLine(
        comparisonLine.map((score, i) => ({
          x: xScale(i),
          y: yScale(score),
        }))
      )
    : "";

  const createSmoothAreaPath = (data: ComparisonArea[]) => {
    const highPoints = data.map((area, i) => ({
      x: xScale(i),
      y: yScale(area.high),
    }));
    const lowPoints = [...data].reverse().map((area, i) => ({
      x: xScale(data.length - 1 - i),
      y: yScale(area.low),
    }));
    const allPoints = [...highPoints, ...lowPoints];
    return `${createSmoothLine(allPoints)} Z`;
  };

  const horizontalLines = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
    y: yScale(ratio * 100),
  }));

  const defaultRenderScore = (column: Column, x: number, y: number) => (
    <>
      <circle cx={x} cy={y} r="4" fill="#0Ea3c3" />
      <text x={x} y={y - 10} textAnchor="middle" fontSize="12" fill="#0Ea3c3">
        {Math.round(column.score)}
      </text>
    </>
  );

  const defaultRenderColumnFooter = (column: Column, x: number, y: number) => (
    <>
      <text x={x} y={y - padding.bottom / 2} textAnchor="middle" fontSize="12">
        {column.title}
      </text>
      <text x={x} y={y - 10} textAnchor="middle" fontSize="16">
        {column.icon}
      </text>
    </>
  );

  return (
    <div ref={containerRef} style={{ height }} className="w-full flex flex-1">
      {(width > 0 || fixedWidth) && (
        <svg width={width} height={height}>
          {comparisonArea && (
            <path
              d={createSmoothAreaPath(comparisonArea)}
              fill="#EDEDED"
              opacity={0.5}
            />
          )}
          {horizontalLines.map(({ y }, index) => (
            <line
              key={index}
              x1={padding.left}
              y1={y}
              x2={width - padding.right}
              y2={y}
              stroke="#E4E6E8"
              strokeWidth="1"
            />
          ))}
          {comparisonLine && (
            <path
              d={comparisonLinePath}
              fill="none"
              stroke="#dce0e5"
              strokeWidth="2"
            />
          )}
          <path d={linePath} fill="none" stroke="#0Ea3c3" strokeWidth="2" />
          {columns.map((col, i) => (
            <g
              key={i}
              onMouseEnter={() => setHoveredColumn(i)}
              onMouseLeave={() => setHoveredColumn(null)}
              onClick={() => onColumnClick && onColumnClick(col)}
              style={{ cursor: onColumnClick ? "pointer" : "default" }}
            >
              <rect
                x={xScale(i) - columnWidth / 2}
                y={padding.top}
                width={columnWidth}
                height={chartHeight}
                fill="transparent"
              />
              {hoveredColumn === i && (
                <g>
                  {/* Main transparent rectangle */}
                  <rect
                    x={xScale(i) - columnWidth / 2}
                    y={padding.top}
                    width={columnWidth}
                    height={chartHeight}
                    fill="transparent"
                    stroke="#E4E6E8"
                    strokeWidth="1"
                  />
                  {/* Top border */}
                  <line
                    x1={xScale(i) - columnWidth / 2}
                    y1={padding.top}
                    x2={xScale(i) + columnWidth / 2}
                    y2={padding.top}
                    stroke="#0Ea3c3"
                    strokeWidth="3"
                  />
                  {/* Left border */}
                  <line
                    x1={xScale(i) - columnWidth / 2}
                    y1={padding.top}
                    x2={xScale(i) - columnWidth / 2}
                    y2={padding.top + chartHeight + padding.bottom}
                    stroke="#E4E6E8"
                    strokeWidth="1"
                  />
                  {/* Right border */}
                  <line
                    x1={xScale(i) + columnWidth / 2}
                    y1={padding.top}
                    x2={xScale(i) + columnWidth / 2}
                    y2={padding.top + chartHeight + padding.bottom}
                    stroke="#E4E6E8"
                    strokeWidth="1"
                  />
                </g>
              )}
              {renderScore ? (
                <foreignObject
                  x={xScale(i) - columnWidth / 2}
                  y={yScale(col.score) - 30}
                  width={columnWidth}
                  height="60"
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {renderScore(col, xScale(i), yScale(col.score))}
                  </div>
                </foreignObject>
              ) : (
                defaultRenderScore(col, xScale(i), yScale(col.score))
              )}
              {renderColumnFooter && (
                <foreignObject
                  x={xScale(i) - columnWidth / 2}
                  y={height - padding.bottom}
                  width={columnWidth}
                  height={padding.bottom}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {renderColumnFooter(col, i, hoveredColumn === i)}
                  </div>
                </foreignObject>
              )}
            </g>
          ))}
          {columns.map((_, i) => {
            if (i === columns.length - 1) return null; // Don't render a line after the last column
            return (
              <line
                key={`footer-separator-${i}`}
                x1={xScale(i) + columnWidth / 2}
                y1={height - padding.bottom}
                x2={xScale(i) + columnWidth / 2}
                y2={height}
                stroke="#E4E6E8"
                strokeWidth="1"
              />
            );
          })}
        </svg>
      )}
    </div>
  );
};

export default LineChart;
